import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionValidationIssue } from './questionValidation';

const ValidationContext = createContext<{ issues: QuestionValidationIssue[]; prefix: string }>({ issues: [], prefix: '' });

export function QuestionValidationProvider({ issues, children }: React.PropsWithChildren<{ issues: QuestionValidationIssue[] }>) {
  return <ValidationContext.Provider value={{ issues, prefix: '' }}>{children}</ValidationContext.Provider>;
}

export function QuestionValidationScope({ prefix, children }: React.PropsWithChildren<{ prefix: string }>) {
  const parent = useContext(ValidationContext);
  return <ValidationContext.Provider value={{ issues: parent.issues, prefix: `${parent.prefix}${prefix}.` }}>{children}</ValidationContext.Provider>;
}

/** Wrap a control or a related control group with its inline validation message. */
export function QuestionField({ name, children, className }: {
  name: string;
  children: React.ReactElement<React.AriaAttributes & { tabIndex?: number; role?: string }>;
  className?: string;
}) {
  const { t } = useTranslation();
  const { issues, prefix } = useContext(ValidationContext);
  const path = `${prefix}${name}`;
  const error = issues.find((issue) => issue.path === path);
  const id = `${useId()}-error`;
  const describedBy = [children.props['aria-describedby'], error ? id : undefined].filter(Boolean).join(' ') || undefined;
  return (
    <div data-question-field={path} data-invalid={Boolean(error)} tabIndex={-1}
      className={`min-w-0 space-y-1 ${error ? '[&>input]:border-destructive [&>textarea]:border-destructive [&>button]:border-destructive [&>select]:border-destructive [&>.question-rich-input]:border-destructive' : ''} ${className ?? ''}`}>
      {React.cloneElement(children, {
        ...(children.type === 'div' ? { role: 'group', tabIndex: -1 } : {}),
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })}
      {error && <p id={id} data-field-error className="text-xs text-destructive">{t(`question_validation_${error.code}`, { defaultValue: error.message })}</p>}
    </div>
  );
}

/** Reveal errors on submit; move focus only on that attempt, never while typing. */
export function useQuestionValidationFeedback(issues: QuestionValidationIssue[]) {
  const formRef = useRef<HTMLDivElement>(null);
  const [attempt, setAttempt] = useState(0);
  const revealErrors = useCallback(() => setAttempt((value) => value + 1), []);
  const resetValidation = useCallback(() => setAttempt(0), []);
  useEffect(() => {
    if (!attempt) return;
    const field = formRef.current?.querySelector<HTMLElement>('[data-question-field][data-invalid="true"]');
    if (!field) return;
    // Rich-text toolbars precede the textbox in DOM order; focus its editable body.
    const input = field.querySelector<HTMLElement>('[contenteditable="true"]')
      ?? field.querySelector<HTMLElement>('input:not([type="hidden"]):not([type="file"]):not(:disabled), textarea:not(:disabled), select:not(:disabled)')
      ?? field.querySelector<HTMLElement>('button:not(:disabled), [role="group"]');
    (input ?? field).focus();
    field.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [attempt]);
  return { formRef, revealErrors, resetValidation, hasAttempted: attempt > 0, visibleIssues: attempt ? issues : [] };
}
